import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { apiSend } from '../lib/api';
import {
  isHomeVariant,
  type CustomBlockConfig,
  type HomeVariant,
} from './blocks/types';
import { defaultCustomLayout } from './variants/presets';

const STORAGE_KEY = 'onedesk.homeVariant';
const LAYOUT_STORAGE_KEY = 'onedesk.customLayout';

// In-tab event bus so the header's variant switcher and the Home page's
// renderer stay in sync without React context. Cross-tab sync still works
// via storage events.
const VARIANT_EVENT = 'onedesk:variant-changed';
const LAYOUT_EVENT = 'onedesk:layout-changed';
const CUSTOMIZE_EVENT = 'onedesk:customize-open';

export const openCustomizeDrawer = () => {
  window.dispatchEvent(new Event(CUSTOMIZE_EVENT));
};

export const useCustomizeListener = (handler: () => void) => {
  useEffect(() => {
    window.addEventListener(CUSTOMIZE_EVENT, handler);
    return () => window.removeEventListener(CUSTOMIZE_EVENT, handler);
  }, [handler]);
};

const readVariant = (): HomeVariant => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (isHomeVariant(v)) return v;
  } catch {
    // localStorage unavailable
  }
  return 'cozy';
};

const readLayout = (): CustomBlockConfig[] => {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as CustomBlockConfig[];
    }
  } catch {
    // ignore
  }
  return defaultCustomLayout();
};

interface PrefsShape {
  homeVariant?: HomeVariant;
  customLayout?: CustomBlockConfig[];
}

export const useHomeVariant = (serverPrefs?: PrefsShape | null) => {
  const { getToken } = useAuth();
  const [variant, setVariantLocal] = useState<HomeVariant>(readVariant);
  const [layout, setLayoutLocal] = useState<CustomBlockConfig[]>(readLayout);

  // Reconcile with server pref on first load (server is authoritative across devices).
  useEffect(() => {
    if (!serverPrefs) return;
    if (serverPrefs.homeVariant && serverPrefs.homeVariant !== variant) {
      setVariantLocal(serverPrefs.homeVariant);
      try {
        localStorage.setItem(STORAGE_KEY, serverPrefs.homeVariant);
      } catch {
        /* ignore */
      }
    }
    if (serverPrefs.customLayout) {
      setLayoutLocal(serverPrefs.customLayout);
      try {
        localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(serverPrefs.customLayout));
      } catch {
        /* ignore */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverPrefs?.homeVariant, JSON.stringify(serverPrefs?.customLayout)]);

  // In-tab sync: any other consumer of this hook gets notified when variant
  // or layout changes. Header switcher → Home renderer without context.
  useEffect(() => {
    const onVariant = (e: Event) => {
      const v = (e as CustomEvent<HomeVariant>).detail;
      if (isHomeVariant(v)) setVariantLocal(v);
    };
    const onLayout = (e: Event) => {
      const next = (e as CustomEvent<CustomBlockConfig[]>).detail;
      if (Array.isArray(next)) setLayoutLocal(next);
    };
    window.addEventListener(VARIANT_EVENT, onVariant);
    window.addEventListener(LAYOUT_EVENT, onLayout);
    return () => {
      window.removeEventListener(VARIANT_EVENT, onVariant);
      window.removeEventListener(LAYOUT_EVENT, onLayout);
    };
  }, []);

  const setVariant = useCallback(
    async (next: HomeVariant) => {
      if (next === variant) return;
      setVariantLocal(next);
      window.dispatchEvent(new CustomEvent<HomeVariant>(VARIANT_EVENT, { detail: next }));
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      try {
        const token = await getToken();
        await apiSend('PATCH', '/api/me/preferences', { homeVariant: next }, token);
      } catch {
        // Local state authoritative — cross-device sync is best-effort.
      }
    },
    [variant, getToken],
  );

  const setLayout = useCallback(
    async (next: CustomBlockConfig[]) => {
      setLayoutLocal(next);
      window.dispatchEvent(new CustomEvent<CustomBlockConfig[]>(LAYOUT_EVENT, { detail: next }));
      try {
        localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      try {
        const token = await getToken();
        await apiSend('PATCH', '/api/me/preferences', { customLayout: next }, token);
      } catch {
        // best-effort
      }
    },
    [getToken],
  );

  return { variant, setVariant, layout, setLayout };
};
